import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { Modal } from "../ui/modal";

/** confirm() 호출 시 넘기는 옵션. 모두 선택값이며 message만 사실상 필수. */
export interface ConfirmOptions {
  title?: string;
  message?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** danger=빨강(삭제 류), default=브랜드색(일반 확인) */
  tone?: "danger" | "default";
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingState extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<PendingState | null>(null);
  // 동시에 여러 confirm 이 겹쳐도 마지막 resolve 가 살아있도록 보관
  const pendingRef = useRef<PendingState | null>(null);
  pendingRef.current = pending;

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      setPending({ ...opts, resolve });
    });
  }, []);

  const close = useCallback((ok: boolean) => {
    const cur = pendingRef.current;
    if (cur) cur.resolve(ok);
    setPending(null);
  }, []);

  const isDanger = pending?.tone === "danger";

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {/* 컨벤션이 명시한 예외 — 열자마자 예/아니오로 닫히는 확인창은 로컬 상태로 둔다
          (ops/workflow/frontend-conventions.md §1). */}
      {/* eslint-disable-next-line modal-url/prefer-url-modal -- 위 주석 참고 */}
      <Modal isOpen={!!pending}
        onClose={() => close(false)}
        showCloseButton={false}
        className="max-w-md p-6"
        // 전역 확인창은 어떤 모달 안에서도 호출되므로 항상 모든 모달(최고 z-[100001]) 위에 온다.
        overlayClassName="z-[100500]"
      >
        {pending && (
          <div>
            {pending.title && (
              <h3 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">
                {pending.title}
              </h3>
            )}
            {pending.message && (
              <div className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                {pending.message}
              </div>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => close(false)}
                className="inline-flex items-center justify-center rounded-lg bg-white px-4 py-2.5 text-sm font-medium text-gray-700 ring-1 ring-inset ring-gray-300 transition hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700 dark:hover:bg-white/[0.03]"
              >
                {pending.cancelText ?? "취소"}
              </button>
              <button
                autoFocus
                onClick={() => close(true)}
                className={`inline-flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-theme-xs transition ${
                  isDanger
                    ? "bg-rose-500 hover:bg-rose-600"
                    : "bg-brand-500 hover:bg-brand-600"
                }`}
              >
                {pending.confirmText ?? "확인"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </ConfirmContext.Provider>
  );
}

/**
 * 전역 확인 다이얼로그 훅. window.confirm 대체용.
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: "삭제", message: "정말 삭제할까요?", tone: "danger" })) { ... }
 */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm 은 ConfirmProvider 안에서만 사용할 수 있습니다.");
  }
  return ctx;
}
